import type { FC } from "react";
import { Dialog, InfoMessageAtom } from "@components";
import { useTranslation } from "react-i18next";
import { TextInput, Label } from "flowbite-react";
import { HiOutlineClipboardCopy, HiOutlineCheck } from "react-icons/hi";
import { useState, useRef } from "react";

export interface ApiKeyCredentials {
  audience: string;
  client_id: string;
  client_secret: string;
  grant_type: string;
}

export interface ApiKeyCredentialsModalProps {
  isShowing: boolean;
  credentials: ApiKeyCredentials | null;
  onClose: () => void;
}

const CopyableField: FC<{ label: string; value: string }> = ({
  label,
  value,
}) => {
  const { t } = useTranslation("common");
  const [copied, setCopied] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleCopy = () => {
    if (!buttonRef.current) return;

    navigator.clipboard
      .writeText(value)
      .then(() => {
        setCopied(true);
        buttonRef.current?.blur();
        setTimeout(() => setCopied(false), 2000);
      })
      .catch((err) => {
        console.error("Failed to copy:", err);
      });
  };

  const baseButtonClasses =
    "min-w-[40px] h-10 flex items-center justify-center p-2 transition-colors duration-150 rounded-lg";
  const normalStateClasses =
    "bg-gray-100 hover:bg-gray-200 border border-gray-300 text-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600 dark:border-gray-600 dark:text-gray-300 focus:outline-none focus:ring-0 focus:ring-offset-0";
  const copiedStateClasses =
    "bg-green-400 hover:bg-green-500 text-white focus:ring-0 focus:ring-offset-0 focus:outline-none";

  return (
    <div className="mb-4">
      <div className="mb-1 block">
        <Label
          htmlFor={label}
          value={label}
          className="text-sm font-semibold text-secondary"
        />
      </div>
      <div className="flex items-center">
        <TextInput
          id={label}
          value={value}
          readOnly
          className="flex-grow mr-2 font-mono text-xs"
          theme={{
            field: {
              input: {
                colors: {
                  gray: "bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:border-blue-500 dark:focus:ring-blue-500 font-mono",
                },
              },
            },
          }}
        />
        <button
          ref={buttonRef}
          type="button"
          onClick={handleCopy}
          aria-label={t("copy_to_clipboard", "Copy")}
          className={`${baseButtonClasses} ${copied ? copiedStateClasses : normalStateClasses}`}
        >
          {copied ? (
            <HiOutlineCheck className="h-5 w-5" />
          ) : (
            <HiOutlineClipboardCopy className="h-5 w-5" />
          )}
        </button>
      </div>
    </div>
  );
};

export const ApiKeyCredentialsModal: FC<ApiKeyCredentialsModalProps> = (
  props,
) => {
  const { t } = useTranslation(["common", "workspace"]);

  if (!props.credentials) {
    return null;
  }

  const dialogContent = (
    <div className="pt-2">
      <InfoMessageAtom
        variant="warning"
        title={t("workspace:api_keys.secret_warning_title", "Important!")}
        className="mb-4"
      >
        {t(
          "workspace:api_keys.secret_warning_message",
          "Your Client Secret is shown only once. Please copy it and store it securely. You will not be able to retrieve it again.",
        )}
      </InfoMessageAtom>

      <CopyableField
        label={t("workspace:api_keys.audience", "Audience")}
        value={props.credentials.audience}
      />
      <CopyableField
        label={t("workspace:api_keys.client_id", "Client ID")}
        value={props.credentials.client_id}
      />
      <CopyableField
        label={t("workspace:api_keys.client_secret", "Client Secret")}
        value={props.credentials.client_secret}
      />
      <CopyableField
        label={t("workspace:api_keys.grant_type", "Grant Type")}
        value={props.credentials.grant_type}
      />
    </div>
  );

  return (
    <Dialog
      title={t(
        "workspace:api_keys.credentials_title",
        "API Key Created Successfully",
      )}
      content={dialogContent}
      show={props.isShowing}
      toggleModal={props.onClose}
      buttonConfirmText={t("common:done", "Done")}
      buttonCloseText={t("common:close", "Close")}
      confirmButton={props.onClose}
      hideButtons={false}
      size="lg"
    />
  );
};
